// @ts-check
// Pseudocódigo PSeInt -> IR-JSON (DEC-107).
//
// NO analiza el pseudocódigo por su cuenta: reutiliza `analizarPseudocodigo`
// del intérprete que ya existe. Escribir un segundo analizador del mismo
// lenguaje garantizaría que algún día "ejecutar" y "traducir" aceptaran
// programas distintos, que es la peor forma de que un alumno descubra que su
// código está mal.
//
// Este módulo solo hace el MAPEO de un árbol a otro.
//
// ## Lo que se niega a traducir, y por qué
//
// - **`Segun` (switch).** La IR no tiene ese nodo. Convertirlo a `Si`
//   anidados cambiaría el diagrama que ve el alumno por uno que él no
//   escribió.
// - **`Escribir a, b`** con varios valores. En pseudocódigo salen en una sola
//   línea; la IR tiene una expresión por `Salida`, y el motor solo concatena
//   con `+` cuando AMBOS lados son texto (`diagram_engine.py`). Traducirlo
//   produciría o bien dos líneas donde había una, o bien un programa que
//   falla al ejecutarse. Se rechaza diciendo qué hacer.
// - **`Dimension` (arreglos).** El tipo `Lista` de la IR necesita el tipo del
//   ELEMENTO y el pseudocódigo declara el TAMAÑO. Son datos distintos.
// - **`^` (potencia)** y funciones propias de PSeInt sin equivalente.

import { analizarPseudocodigo } from '../pseudocode-editor/pseint-interpreter.js';
import { crearRegistroDeMotivos, traduccionFallida, traduccionOk } from './resultado.js';

/** Tipos de `Definir ... Como X` -> tipo primitivo de la IR. */
const TIPO_IR = /** @type {Record<string, string>} */ ({
  entero: 'entero',
  real: 'decimal',
  numero: 'decimal',
  numerico: 'decimal',
  caracter: 'cadena',
  cadena: 'cadena',
  texto: 'cadena',
  logico: 'booleano',
  booleano: 'booleano',
});

/** Operadores del árbol de PSeInt -> `Operador` de la IR. */
const OPERADOR_IR = /** @type {Record<string, string>} */ ({
  '+': '+',
  '-': '-',
  '*': '*',
  '/': '/',
  '%': '%',
  mod: '%',
  '=': '==',
  '==': '==',
  '<>': '!=',
  '!=': '!=',
  '<': '<',
  '<=': '<=',
  '>': '>',
  '>=': '>=',
  y: 'Y',
  '&': 'Y',
  o: 'O',
  '|': 'O',
});

/** @param {string} s */
function sinAcentos(s) {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

/**
 * @param {any} nodo
 * @param {ReturnType<typeof crearRegistroDeMotivos>} registro
 * @returns {any}
 */
function expresion(nodo, registro) {
  if (!nodo || typeof nodo !== 'object') {
    registro.anotar('Hay una expresión vacía.');
    return { nodo: 'Literal', valor: 0, tipo: 'entero' };
  }
  switch (nodo.tipo) {
    case 'numero':
      return {
        nodo: 'Literal',
        valor: nodo.valor,
        tipo: Number.isInteger(nodo.valor) ? 'entero' : 'decimal',
      };
    case 'cadena':
      return { nodo: 'Literal', valor: String(nodo.valor), tipo: 'cadena' };
    case 'logico':
      return { nodo: 'Literal', valor: Boolean(nodo.valor), tipo: 'booleano' };
    case 'variable':
      return { nodo: 'ReferenciaVariable', nombre: String(nodo.nombre) };
    case 'unaria': {
      const operador = nodo.op === 'no' ? 'NO' : 'NEG';
      return { nodo: 'Operacion', operador, operandos: [expresion(nodo.valor, registro)] };
    }
    case 'binaria': {
      const clave = sinAcentos(String(nodo.op));
      const operador = OPERADOR_IR[clave];
      if (!operador) {
        registro.anotar(
          `El operador "${nodo.op}" no existe en los diagramas.`,
          `línea ${nodo.linea}`,
        );
        return { nodo: 'Literal', valor: 0, tipo: 'entero' };
      }
      return {
        nodo: 'Operacion',
        operador,
        operandos: [expresion(nodo.izq, registro), expresion(nodo.der, registro)],
      };
    }
    case 'llamada':
      return {
        nodo: 'Llamada',
        nombre: String(nodo.nombre),
        argumentos: (nodo.args ?? []).map((/** @type {any} */ a) => expresion(a, registro)),
      };
    case 'indice': {
      const indices = nodo.indices ?? [];
      if (indices.length !== 1) {
        registro.anotar(
          'Los arreglos de más de una dimensión no existen en los diagramas.',
          `línea ${nodo.linea}`,
        );
        return { nodo: 'Literal', valor: 0, tipo: 'entero' };
      }
      return {
        nodo: 'AccesoIndice',
        lista: { nodo: 'ReferenciaVariable', nombre: String(nodo.nombre) },
        indice: expresion(indices[0], registro),
      };
    }
    default:
      registro.anotar(`No sé convertir la expresión "${nodo.tipo}".`, `línea ${nodo.linea}`);
      return { nodo: 'Literal', valor: 0, tipo: 'entero' };
  }
}

/**
 * @param {any[]} sentencias
 * @param {ReturnType<typeof crearRegistroDeMotivos>} registro
 * @param {any[]} declaraciones Se rellena con las `Definir` encontradas.
 * @returns {any}
 */
function secuencia(sentencias, registro, declaraciones) {
  /** @type {any[]} */
  const salida = [];
  for (const s of sentencias ?? []) {
    const traducida = unaSentencia(s, registro, declaraciones);
    if (Array.isArray(traducida)) salida.push(...traducida);
    else if (traducida) salida.push(traducida);
  }
  return { nodo: 'Secuencia', sentencias: salida };
}

/**
 * @param {any} s
 * @param {ReturnType<typeof crearRegistroDeMotivos>} registro
 * @param {any[]} declaraciones
 * @returns {any}
 */
function unaSentencia(s, registro, declaraciones) {
  if (!s || typeof s !== 'object') return null;
  const donde = `línea ${s.linea}`;
  switch (s.tipo) {
    case 'definir': {
      const clave = sinAcentos(String(s.tipoDeclarado ?? ''));
      const tipo = TIPO_IR[clave];
      if (!tipo) {
        registro.anotar(
          s.tipoDeclarado
            ? `El tipo "${s.tipoDeclarado}" no existe en los diagramas.`
            : 'Falta el tipo en un "Definir".',
          donde,
        );
        return null;
      }
      // Las declaraciones suben al `Programa`, que es donde las guarda la IR,
      // en vez de quedarse dentro del cuerpo.
      for (const nombre of s.nombres ?? []) {
        declaraciones.push({ nodo: 'Declaracion', nombre: String(nombre), tipo });
      }
      return null;
    }
    case 'dimension':
      registro.anotar(
        'Los arreglos declarados con "Dimension" no se pueden convertir todavía.',
        donde,
      );
      return null;
    case 'escribir': {
      const partes = s.partes ?? [];
      if (partes.length !== 1) {
        registro.anotar(
          'Un "Escribir" con varios valores separados por comas no se puede convertir: escribe una sola expresión por línea.',
          donde,
        );
        return null;
      }
      return { nodo: 'Salida', expresion: expresion(partes[0], registro) };
    }
    case 'leer': {
      const destinos = s.destinos ?? [];
      /** @type {any[]} */
      const entradas = [];
      for (const destino of destinos) {
        if (destino?.tipo !== 'variable') {
          registro.anotar('Solo se puede leer sobre una variable simple.', donde);
          continue;
        }
        // `Leer a, b` SÍ se puede desdoblar sin cambiar el comportamiento: son
        // dos lecturas consecutivas, igual que dos `Leer` seguidos. (A
        // diferencia de `Escribir a, b`, que sí cambiaría la salida.)
        entradas.push({ nodo: 'Entrada', variable: String(destino.nombre) });
      }
      return entradas;
    }
    case 'asignacion': {
      const destino = s.destino;
      if (destino?.tipo !== 'variable') {
        registro.anotar(
          'Asignar sobre una posición de un arreglo no se puede convertir todavía.',
          donde,
        );
        return null;
      }
      return {
        nodo: 'Asignacion',
        variable: String(destino.nombre),
        expresion: expresion(s.valor, registro),
      };
    }
    case 'llamadaSentencia': {
      const llamada = s.llamada;
      return {
        nodo: 'Llamada',
        nombre: String(llamada?.nombre ?? ''),
        argumentos: (llamada?.args ?? []).map((/** @type {any} */ a) => expresion(a, registro)),
      };
    }
    case 'si': {
      const sino = s.sino ?? [];
      const resultado = {
        nodo: 'Condicional',
        condicion: expresion(s.condicion, registro),
        rama_si: secuencia(s.entonces, registro, declaraciones),
      };
      if (sino.length > 0) {
        return { ...resultado, rama_sino: secuencia(sino, registro, declaraciones) };
      }
      return resultado;
    }
    case 'mientras':
      return {
        nodo: 'BucleMientras',
        condicion: expresion(s.condicion, registro),
        cuerpo: secuencia(s.cuerpo, registro, declaraciones),
      };
    case 'para':
      return {
        nodo: 'BucleFinPara',
        variable: String(s.variable),
        inicio: expresion(s.desde, registro),
        fin: expresion(s.hasta, registro),
        // Sin "Con Paso" el paso es 1, que es lo que hace PSeInt.
        paso: s.paso ? expresion(s.paso, registro) : { nodo: 'Literal', valor: 1, tipo: 'entero' },
        cuerpo: secuencia(s.cuerpo, registro, declaraciones),
      };
    case 'repetir':
      return {
        nodo: 'RepetirHasta',
        cuerpo: secuencia(s.cuerpo, registro, declaraciones),
        condicion: expresion(s.condicion, registro),
      };
    case 'segun':
      registro.anotar(
        'La instrucción "Segun" no existe en los diagramas. Escríbela con "Si" anidados si quieres convertirla.',
        donde,
      );
      return null;
    default:
      registro.anotar(`No sé convertir la instrucción "${s.tipo}".`, donde);
      return null;
  }
}

/**
 * Traduce pseudocódigo PSeInt a IR-JSON.
 * @param {string} codigo Texto completo (algoritmo principal + subprocesos).
 * @returns {import('./resultado.js').Traduccion<any>}
 */
export function pseudocodigoAIr(codigo) {
  if (String(codigo ?? '').trim() === '') {
    return traduccionFallida([{ descripcion: 'No hay pseudocódigo que convertir.' }]);
  }

  /** @type {{ principal: any[], subprogramas: Map<string, any> }} */
  let arbol;
  try {
    arbol = analizarPseudocodigo(codigo);
  } catch (error) {
    const mensaje = /** @type {{ message?: string, linea?: number|null }} */ (error ?? {});
    return traduccionFallida([
      {
        descripcion: `El pseudocódigo tiene un error y no se puede convertir: ${mensaje.message ?? 'error desconocido'}`,
        ...(mensaje.linea ? { donde: `línea ${mensaje.linea}` } : {}),
      },
    ]);
  }

  const registro = crearRegistroDeMotivos();
  /** @type {any[]} */
  const declaraciones = [];
  const cuerpo = secuencia(arbol.principal, registro, declaraciones);

  /** @type {any[]} */
  const funciones = [];
  for (const [nombre, sub] of arbol.subprogramas ?? new Map()) {
    /** @type {any[]} */
    const declaracionesSub = [];
    const cuerpoSub = secuencia(sub.cuerpo, registro, declaracionesSub);
    // Las `Definir` de dentro de un subproceso son LOCALES: se quedan al
    // principio de su cuerpo en vez de subir al programa, o pasarían a ser
    // globales y cambiarían el significado.
    cuerpoSub.sentencias.unshift(...declaracionesSub);
    // En PSeInt el valor devuelto es el que tenga la variable de la cabecera
    // al terminar; no hay instrucción de retorno. La IR sí la tiene, así que
    // se añade al final para que el significado se conserve.
    if (sub.retorno) {
      cuerpoSub.sentencias.push({
        nodo: 'Retorno',
        expresion: { nodo: 'ReferenciaVariable', nombre: String(sub.retorno) },
      });
    }
    funciones.push({
      nodo: 'Funcion',
      nombre: String(nombre),
      // `params` es el nombre del campo en el árbol del intérprete.
      // El tipo se fija a `entero` porque PSeInt no declara el de los
      // parámetros y la IR sí lo exige: es el único punto de este traductor
      // donde se pone un valor que el original no dice, y queda anotado
      // aquí para que nadie lo lea como un dato real del programa.
      parametros: (sub.params ?? []).map((/** @type {any} */ p) => ({
        nodo: 'Parametro',
        nombre: String(p?.nombre ?? ''),
        tipo: 'entero',
      })),
      cuerpo: cuerpoSub,
      // PSeInt no declara el tipo de retorno. Se marca como función que
      // devuelve valor cuando la cabecera tiene variable de retorno.
      tipo_retorno: sub.retorno ? 'entero' : null,
    });
  }

  if (registro.hayProblemas()) return traduccionFallida(registro.motivos());
  return traduccionOk({ nodo: 'Programa', declaraciones, funciones, cuerpo });
}
